<script lang="ts">
  import { setContext } from "svelte";
  import type { Writable } from "svelte/store";
  import * as array from "./array";
  import DialogCopyMoveFile from "./DialogCopyMoveFile.svelte";
  import DialogDelete from "./DialogDelete.svelte";
  import DialogDownload from "./DialogDownload.svelte";
  import DialogFileProperties from "./DialogFileProperties.svelte";
  import DialogNewDirectory from "./DialogNewDirectory.svelte";
  import DialogRename from "./DialogRename.svelte";
  import DialogUploadFiles from "./DialogUploadFiles.svelte";
  import type { FileListCmdApi } from "./file-list-api";
  import * as filepath from "./filepath";
  import type { FS, FSDirState } from "./fs";
  import {
    FileInfo,
    fsFullDir,
    fsTypeAzureBlob,
    fsTypeBox,
    fsTypeDrime,
    fsTypeDropbox,
    fsTypeFichier,
    fsTypeFtp,
    fsTypeGcs,
    fsTypeGDrive,
    fsTypeGofile,
    fsTypeJottacloud,
    fsTypeKoofr,
    fsTypeLinkbox,
    fsTypeLocal,
    fsTypeMega,
    fsTypeOneDrive,
    fsTypePixeldrain,
    fsTypeQuatrix,
    fsTypeS3,
    fsTypeSeafile,
    fsTypeSftp,
    fsTypeSmb,
    fsTypeStorj,
    fsTypeSwift,
    fsTypeUlozto,
    fsTypeWebdav,
    fsTypeYandex,
  } from "./fs";
  import { CopyInfo, fsCopyMove } from "./fs-copy";
  import type { FsRow } from "./fsentry";
  import { captureEvent } from "./http";
  import { MenuItem, menuItemSeparator } from "./MenuItem";
  import { showTemporaryMessage } from "./message";
  import Overlay from "./Overlay.svelte";
  import ShowImage from "./ShowImage.svelte";
  import ShowText from "./ShowText.svelte";
  import { canCopyMove } from "./store";
  import { copyTextToClipboard, escHtml, getAllFileEntries, inflect, len } from "./util";

  interface Props {
    fs: FS;
    fsState: FSDirState;
    progressMsg: Writable<any>;
    pickOtherFSState: (fsState: FSDirState) => FSDirState;
    errorMsg: string;
    getSelected: () => Record<string, FsRow>;
    findEntryIdxById: (id: string) => number;
    isSelected: (idx: number) => boolean;
    makeSingleSelected: (idx: number) => void;
    getEntryFromContextMenuEvent: (ev: MouseEvent) => FsRow | null;
    reloadCurrentDir: () => void;
    fsOnError: (err: any) => void;
    cmds: FileListCmdApi;
    currentContextMenu: any;
    cmPageX: number;
    cmPageY: number;
  }

  let {
    fs,
    fsState,
    progressMsg,
    pickOtherFSState,
    errorMsg = $bindable(""),
    getSelected,
    findEntryIdxById,
    isSelected,
    makeSingleSelected,
    getEntryFromContextMenuEvent,
    reloadCurrentDir,
    fsOnError,
    cmds,
    currentContextMenu = $bindable(),
    cmPageX = $bindable(0),
    cmPageY = $bindable(0),
  }: Props = $props();

  let showingTextPreview = $state(false);
  let showingImagePreview = $state(false);
  let textPreviewURL: string = $state("");
  let textPreviewName: string = $state("");
  let imagePreviewURL: string = $state("");
  let imagePreviewName: string = $state("");
  let urlToInvoke: string = "";

  let filesEl = $state<HTMLInputElement>();

  const imgExts = [".avif", ".bmp", ".gif", ".jpg", ".jpeg", ".png", ".webp", ".svg"];

  const txtExts = [
    ".bat",
    ".clang",
    ".clang-format",
    ".clang-tidy",
    ".c",
    ".cpp",
    ".cppcheck",
    ".css",
    ".csv",
    ".cxx",
    ".editorconfig",
    ".filters",
    ".gitattributes",
    ".gitignore",
    ".gitmodules",
    ".h",
    ".hxx",
    ".hpp",
    ".html",
    ".ini",
    ".js",
    ".log",
    ".py",
    ".pylintrc",
    ".md",
    ".sh",
    ".sln",
    ".tex",
    ".txt",
    ".vcxproj",
    ".yml",
    ".yaml",
    ".xml",
  ];

  function fileHasExt(s: string, exts: string[]) {
    s = s.toLowerCase();
    for (let ext of exts) {
      if (s.endsWith(ext)) {
        return true;
      }
    }
    return false;
  }

  function entriesWithExts(entries: FsRow[], exts: string[]) {
    let files = [];
    for (let e of entries) {
      if (e.isDir) {
        continue;
      }
      if (!fileHasExt(e.name, exts)) {
        continue;
      }
      files.push(e);
    }
    return files;
  }

  function hideImagePreview() {
    console.log("hideImagePreview");
    showingImagePreview = false;
    if (urlToInvoke) {
      URL.revokeObjectURL(urlToInvoke);
      urlToInvoke = "";
    }
  }

  function hideTextPreview() {
    console.log("hideTextPreview");
    showingTextPreview = false;
    textPreviewName = "";
    if (urlToInvoke) {
      URL.revokeObjectURL(urlToInvoke);
      urlToInvoke = "";
    }
  }

  async function previewTextFile(file: FsRow) {
    let fileName = file.name;
    let path = filepath.join(fsState.currDir, fileName);
    progressMsg.set(`previewing text ${path}`);
    let url;
    try {
      url = await fs.getPublicURL(path);
    } catch (e: any) {
      errorMsg = e.toString();
      progressMsg.set("");
      return;
    }

    progressMsg.set("");
    textPreviewURL = url;
    textPreviewName = fileName;
    if (
      fs.type == fsTypeLocal ||
      fs.type == fsTypePixeldrain ||
      fs.type == fsTypeSftp ||
      fs.type == fsTypeFtp ||
      fs.type == fsTypeSmb ||
      fs.type == fsTypeGDrive ||
      fs.type == fsTypeWebdav ||
      fs.type == fsTypeBox ||
      fs.type == fsTypeMega ||
      fs.type == fsTypeKoofr ||
      fs.type == fsTypeSeafile ||
      fs.type == fsTypeYandex ||
      fs.type == fsTypeJottacloud ||
      fs.type == fsTypeSwift ||
      fs.type == fsTypeFichier ||
      fs.type == fsTypeGofile ||
      fs.type == fsTypeDrime ||
      fs.type == fsTypeQuatrix ||
      fs.type == fsTypeLinkbox ||
      fs.type == fsTypeUlozto
    ) {
      urlToInvoke = url;
    }
    let ext = filepath.ext(fileName);
    showingTextPreview = true;
    captureEvent("previewTextFile", { fs: fs.type, ext: ext });
  }

  async function previewImageFile(file: FsRow) {
    let fileName = file.name;
    let path = filepath.join(fsState.currDir, fileName);
    progressMsg.set(`previewing image ${path}`);
    let url;

    try {
      url = await fs.getPublicURL(path);
    } catch (e: any) {
      progressMsg.set("");
      errorMsg = e.toString();
      console.log("previewImageFile: getPublicURL failed with ", e);
      return;
    }
    progressMsg.set("");
    imagePreviewURL = url;
    imagePreviewName = fileName;
    if (
      fs.type == fsTypeLocal ||
      fs.type == fsTypePixeldrain ||
      fs.type == fsTypeSftp ||
      fs.type == fsTypeFtp ||
      fs.type == fsTypeSmb ||
      fs.type == fsTypeGDrive ||
      fs.type == fsTypeWebdav ||
      fs.type == fsTypeBox ||
      fs.type == fsTypeMega ||
      fs.type == fsTypeKoofr ||
      fs.type == fsTypeSeafile ||
      fs.type == fsTypeYandex ||
      fs.type == fsTypeJottacloud ||
      fs.type == fsTypeSwift ||
      fs.type == fsTypeFichier ||
      fs.type == fsTypeGofile ||
      fs.type == fsTypeDrime ||
      fs.type == fsTypeQuatrix ||
      fs.type == fsTypeLinkbox ||
      fs.type == fsTypeUlozto
    ) {
      urlToInvoke = url;
    }
    let ext = filepath.ext(fileName);
    showingImagePreview = true;
    captureEvent("previewImageFile", { fs: fs.type, ext: ext });
  }

  function cmdViewFile() {
    console.log("cmdViewFile:");
    let files = contextMenuViewableImageFiles;
    if (len(files) > 0) {
      previewImageFile(files[0]);
      return;
    }
    files = contextMenuViewableTextFiles;
    console.log("viewableTextFiles:", files);
    if (len(files) > 0) {
      previewTextFile(files[0]);
      return;
    }
  }

  function openFile(e: FsRow) {
    if (fileHasExt(e.name, imgExts)) {
      previewImageFile(e);
      return;
    }
    if (fileHasExt(e.name, txtExts)) {
      previewTextFile(e);
      return;
    }
  }

  function cmdRefresh() {
    console.log("cmdRefresh:");
    // also the sizes of the directories are found again
    fsState.fs.dirStats.invalidateTree(fsState.currDir);
    reloadCurrentDir();
  }

  function cmdCopyFileNames() {
    console.log("cmCopyFileName:");
    let items = contextMenuEntries;
    if (len(items) == 0) {
      return;
    }
    let names = [];
    for (let e of items) {
      names.push(e.name);
    }
    let s = names.join("\n");
    copyTextToClipboard(s);
    let n = len(names);
    showTemporaryMessage(`Copied ${n} file ${inflect("name", n)} to clipboard`);
    captureEvent("copyFileNames", { fs: fs.type, count: n });
  }

  function cmdCopyFilePaths() {
    console.log("cmdCopyFilePaths:");
    let items = contextMenuEntries;
    if (len(items) == 0) {
      return;
    }
    let paths = [];
    for (let e of items) {
      paths.push(filepath.join(fsState.currDir, e.name));
    }
    let s = paths.join("\n");
    copyTextToClipboard(s);
    let n = len(paths);
    showTemporaryMessage(`Copied ${n} file ${inflect("path", n)} to clipboard`);
    captureEvent("copyFilePaths", { fs: fs.type, count: n });
  }

  let showDialogNewDirectory = $state(false);

  function cmdUploadFiles() {
    filesEl?.click();
  }

  function cmdNewDirectory() {
    console.log("cmdNewDirectory");
    showDialogNewDirectory = true;
  }

  function newDirectoryAllowNested() {
    return fs.type == fsTypeLocal;
  }

  function doNewDirectory(dirName: string) {
    showDialogNewDirectory = false;
    console.log(`doNewDirectory: ${dirName}`);
    progressMsg.set(`Creating new directory ${dirName}`);
    fs.createDirectory(dirName).then(onDone).catch(fsOnError);
    function onDone() {
      console.log(`finished creating new directory`);
      progressMsg.set("");
      reloadCurrentDirMaybeDelayed();
      captureEvent("createDirectory", { fs: fs.type });
    }
  }

  let entryToRename: FsRow | undefined;
  let showingFileRename = $state(false);
  let filePathToRename: string = $state("");
  let fileRenameIgnoreCase = $state(false);
  let fileRenameSameDir = $state(false);

  function cmdRenameFile() {
    console.log("cmdRenameFile:");

    fileRenameIgnoreCase = false;
    switch (fs.type) {
      case fsTypeDropbox:
      case fsTypeLocal:
        fileRenameIgnoreCase = true;
    }

    fileRenameSameDir = false;
    switch (fs.type) {
      case fsTypeLocal:
      case fsTypeOneDrive:
        fileRenameSameDir = true;
    }
    filePathToRename = filepath.join(fsState.currDir, entryToRename?.name ?? "");
    showingFileRename = true;
  }

  setContext("fnRename", doRename);

  function doRename(o: { path: string; newPath: string }) {
    showingFileRename = false;
    filePathToRename = "";
    let { path, newPath } = o;
    console.log(`doRename: path=${path}, newPath=${newPath}`);
    progressMsg.set(`renaming ${path} to ${newPath}`);
    fs.rename(path, newPath).then(onDone).catch(fsOnError);
    function onDone() {
      console.log(`finished renaming files`);
      captureEvent("renameFiles", { fs: fs.type, path, newPath });
      progressMsg.set("");
      reloadCurrentDirMaybeDelayed();
    }
  }

  let showingFileProperties = $state(false);
  let filePropertiesEntry = $state<FsRow>();
  let filePropertiesPath: string = $state("");

  function cmdFileProperties() {
    console.log("cmdFileProperties");
    let e = firstFileEntry;
    if (!e) {
      return;
    }
    filePropertiesPath = filepath.join(fsState.currDir, e.name);
    filePropertiesEntry = e;
    showingFileProperties = true;
  }

  let showDialogDownload = $state(false);
  let filesToDownload = $state<string[]>([]);

  function cmdDownloadFiles() {
    console.log("cmdDownloadFiles");
    let items = contextMenuFiles;
    if (len(items) == 0) {
      console.log("cmdDownloadFiles: no selected files");
      return;
    }
    let paths = [];
    for (let e of items) {
      paths.push(filepath.join(fsState.currDir, e.name));
    }
    filesToDownload = paths;
    showDialogDownload = true;
  }

  function triggerURLDownload(url: string, name: string) {
    console.log("triggerURLDownload: url", url, "name:", name);
    let el = document.createElement("a");
    el.setAttribute("href", url);
    el.setAttribute("download", name);
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  }

  setContext("fnDownloadFiles", doDownload);

  async function doDownload(paths: string[]) {
    showDialogDownload = false;
    let nFiles = len(paths);
    console.log("doDownload:", paths, "nFiles:", nFiles);

    for (let path of filesToDownload) {
      progressMsg.set(`downloading ${path}`);
      let fileName = fs.downloadName(path);

      let url;
      try {
        url = await fs.getPublicURL(path);
      } catch (e: any) {
        console.log(`doDownload: getPublicURL failed with`, e);
        progressMsg.set("");
        errorMsg = e.toString();
        return;
      }
      progressMsg.set("");
      triggerURLDownload(url, fileName);
    }

    captureEvent("downloadFiles", { fs: fs.type, count: nFiles });
  }

  let showDialogDelete = $state(false);
  let filesToDelete: FileInfo[] = $state([]);

  function cmdDeleteFiles() {
    console.log("cmdDeleteFiles:");
    let items = contextMenuEntries;
    if (len(items) == 0) {
      console.log("cmdDeleteFiles: no selected files");
      return;
    }
    let paths = [];
    for (let e of items) {
      let path = filepath.join(fsState.currDir, e.name);
      if (e.isDir) {
        path += "/";
      }
      let fi = new FileInfo();
      fi.path = path;
      fi.name = e.name;
      fi.size = e.size;
      paths.push(fi);
    }
    filesToDelete = paths;
    showDialogDelete = true;
  }

  function reloadCurrentDirMaybeDelayed() {
    let delay =
      fs.type == fsTypeS3 ||
      fs.type == fsTypeStorj ||
      fs.type == fsTypeAzureBlob ||
      fs.type == fsTypeGcs ||
      fs.type == fsTypeSwift;
    if (delay) {
      setTimeout(() => {
        reloadCurrentDir();
      }, 500);
    } else {
      reloadCurrentDir();
    }
  }

  function doDeleteFiles(paths: string[]) {
    showDialogDelete = false;
    console.log("doDelete:", paths);
    let nFiles = len(paths);
    progressMsg.set(`deleting ${nFiles} ${inflect("file", nFiles)}`);
    fs.deleteFiles(paths, progressMsg).then(onDone).catch(fsOnError);
    function onDone() {
      progressMsg.set("");
      console.log(`finished deleting files`);
      reloadCurrentDirMaybeDelayed();
      captureEvent("deleteFiles", { fs: fs.type, count: nFiles });
    }
  }

  let contextMenuEntries: FsRow[] = [];
  let contextMenuDirs: FsRow[] = [];
  let contextMenuFiles: FsRow[] = [];
  let contextMenuViewableTextFiles: FsRow[] = [];
  let contextMenuViewableImageFiles: FsRow[] = [];
  let firstFileEntry: FsRow | undefined;

  function calcContextMenuSelectedEntries(e: FsRow | null) {
    contextMenuEntries = [];
    contextMenuDirs = [];
    contextMenuFiles = [];

    for (let e of Object.values(getSelected())) {
      if (e.name == "..") {
        continue;
      }
      contextMenuEntries.push(e);
      if (e.isDir) {
        contextMenuDirs.push(e);
      } else {
        contextMenuFiles.push(e);
      }
    }
    if (e) {
      if (e.isDir) {
        array.pushUnique(contextMenuDirs, e);
      } else {
        array.pushUnique(contextMenuFiles, e);
      }
    }

    contextMenuViewableTextFiles = entriesWithExts(contextMenuFiles, txtExts);
    contextMenuViewableImageFiles = entriesWithExts(contextMenuFiles, imgExts);
  }

  function onContextMenu(ev: MouseEvent) {
    showContextMenu(ev.pageX, ev.pageY, getEntryFromContextMenuEvent(ev));
  }

  // e is the row the menu was asked for, if any
  function showContextMenu(pageX: number, pageY: number, e: FsRow | null) {
    cmPageX = pageX;
    cmPageY = pageY;

    if (e) {
      let idx = findEntryIdxById(e.id);
      if (idx >= 0 && !isSelected(idx)) {
        makeSingleSelected(idx);
      }
    }
    currentContextMenu = buildMenu(e);
  }

  // Returns the menu of the selection, and of row e if there is one. Also
  // what the palette lists as commands.
  function buildMenu(e: FsRow | null): MenuItem[] {
    calcContextMenuSelectedEntries(e);

    let cm = [new MenuItem("Refresh", cmdRefresh)];

    let nFiles = len(contextMenuFiles);
    let nDirs = len(contextMenuDirs);
    let nEntries = nDirs + nFiles;
    firstFileEntry = nFiles > 0 ? contextMenuFiles[0] : undefined;
    let firstEntry: FsRow | undefined = nEntries > 0 ? contextMenuEntries[0] : undefined;
    let nViewable = len(contextMenuViewableTextFiles) + len(contextMenuViewableImageFiles);
    let firstViewableEntry: FsRow | undefined;
    if (len(contextMenuViewableImageFiles) > 0) {
      firstViewableEntry = contextMenuViewableImageFiles[0];
    } else if (len(contextMenuViewableTextFiles) > 0) {
      firstViewableEntry = contextMenuViewableTextFiles[0];
    }

    let filesStr = `<span class="text-blue-700">`;
    if (nDirs > 0) {
      filesStr += `${nDirs} ${inflect("dir", nDirs)} `;
    }
    if (nFiles > 0) {
      filesStr += `${nFiles} ${inflect("file", nFiles)} `;
    }
    filesStr += `</span>`;

    let label;
    let mi;

    if (nViewable > 0) {
      label = `View  <span class="text-blue-700">${escHtml(firstViewableEntry?.name ?? "")}</span>`;
      mi = new MenuItem(label, cmdViewFile);
      cm.push(mi);
    }

    if (nEntries > 0) {
      cm.push(menuItemSeparator);
      label = "Copy File Name";
      if (nEntries > 1) {
        label = `Copy ${nEntries} File Names`;
      }
      mi = new MenuItem(label, cmdCopyFileNames);
      cm.push(mi);
      label = "Copy File Path";
      if (nEntries > 1) {
        label = `Copy ${nEntries} File Paths`;
      }
      mi = new MenuItem(label, cmdCopyFilePaths);
      cm.push(mi);
    }

    if (nFiles > 0) {
      label = `Properties of <span class="text-blue-700">${escHtml(firstFileEntry?.name ?? "")}</span>`;
      mi = new MenuItem(label, cmdFileProperties);
      cm.push(mi);
    }
    cm.push(menuItemSeparator);

    mi = new MenuItem("Upload Files From Computer", cmdUploadFiles);
    cm.push(mi);

    if (nFiles > 0) {
      label = `Download <span class="text-blue-700">${escHtml(firstFileEntry?.name ?? "")}</span>`;
      if (nFiles > 1) {
        label = `Download  <span class="text-blue-700">${nFiles} Files</span>`;
      }
      mi = new MenuItem(label, cmdDownloadFiles);
      cm.push(mi);
    }

    if ($canCopyMove && nEntries > 0) {
      label = `Copy <span class="text-blue-700">${escHtml(firstEntry?.name ?? "")}</span>`;
      if (nEntries > 1) {
        label = `Copy ` + filesStr;
      }
      mi = new MenuItem(label, cmdCopyFiles);
      cm.push(mi);

      label = `Move <span class="text-blue-700">${escHtml(firstEntry?.name ?? "")}</span>`;
      if (nEntries > 1) {
        label = `Move ` + filesStr;
      }
      mi = new MenuItem(label, cmdMoveFiles);
      cm.push(mi);
    }

    if (nEntries > 0) {
      entryToRename = undefined;
      if (fsState.fs.supportsDirRename) {
        entryToRename = firstEntry;
      } else if (nFiles > 0) {
        entryToRename = firstFileEntry;
      }
      if (entryToRename) {
        label = `Rename <span class="text-blue-700">${escHtml(entryToRename.name)}</span>`;
        mi = new MenuItem(label, cmdRenameFile);
        cm.push(mi);
      }
    }

    mi = new MenuItem("New Directory", cmdNewDirectory);
    cm.push(mi);

    if (nEntries > 0) {
      label = `Delete <span class="text-blue-700">${escHtml(firstEntry?.name ?? "")}</span>`;
      if (nEntries > 1) {
        label = `Delete ` + filesStr;
      }
      mi = new MenuItem(label, cmdDeleteFiles);
      cm.push(mi);
    }

    return cm;
  }

  let showDialogUploadFiles = $state(false);
  let filesToUpload: FileInfo[] = $state([]);

  function filesSelected(ev: Event & { currentTarget: EventTarget & HTMLInputElement }) {
    if (!filesEl || !filesEl.files) {
      return;
    }
    filesToUpload = [];
    for (let f of Array.from(filesEl.files)) {
      let fi = new FileInfo();
      fi.file = f;
      fi.name = f.name;
      fi.size = f.size;
      filesToUpload.push(fi);
      console.log(`file: ${f.name}, fullName: ${fi.name}`);
    }
    showDialogUploadFiles = true;
  }

  setContext("fnUploadFiles", doUpload);

  function doUpload(ev: any) {
    console.log("doUpload()", filesToUpload);
    showDialogUploadFiles = false;
    let files: FileInfo[] = [];
    for (let fi of filesToUpload) {
      console.log("fi:", fi);
      fi.dstPath = filepath.join(fsState.currDir, fi.name);
      files.push(fi);
    }
    fs.uploadFiles(files, progressMsg).then(onDone).catch(fsOnError);
    function onDone() {
      progressMsg.set("");
      reloadCurrentDir();
      console.log("upload finished");
    }
  }

  async function onFileDrop(ev: Event) {
    console.log("onFileDrop()");
    const dt = (ev as DragEvent).dataTransfer;
    if (!dt) {
      return;
    }
    let dropped = await getAllFileEntries(dt.items);
    filesToUpload = [];
    for (let fe of dropped) {
      console.log("fe:", fe);
      let f = await new Promise<File>((resolve, reject) => {
        (fe as FileSystemFileEntry).file(resolve, reject);
      });
      let path = fe.fullPath;
      if (path.startsWith("/")) {
        path = path.substring(1);
      }
      let fi = new FileInfo();
      fi.file = f;
      fi.name = f.name;
      fi.size = f.size;
      fi.path = path;
      console.log(`file: ${f.name}, type: ${f.type}`);
      filesToUpload.push(fi);
    }
    showDialogUploadFiles = true;
  }

  let showwingCopyMoveFiles = $state(false);
  let entriesToCopy: FsRow[] = $state<FsRow[]>([]);
  let copyFSStateFrom: FSDirState | null = null;
  let copyFsFrom: FS | undefined;
  let copyFSStateTo: FSDirState | null = null;
  let copyFsTo: FS | undefined;
  let copyFromDir = $state("");
  let copyToDir = $state<string>();
  // the dirs with their file systems, for showing
  let copyFromFull = $state("");
  let copyToFull = $state("");
  let isMove = $state(false);

  function cmdCopyMoveFiles() {
    console.log(`cmdCopyMoveFiles: isMove:${isMove}`);
    copyFSStateFrom = fsState;
    copyFsFrom = fsState.fs;
    copyFromDir = fsState.currDir;
    copyFSStateTo = pickOtherFSState(fsState);
    copyFsTo = copyFSStateTo?.fs;
    copyToDir = copyFSStateTo?.currDir;
    copyFromFull = fsFullDir(copyFsFrom, copyFromDir);
    copyToFull = copyFsTo ? fsFullDir(copyFsTo, copyToDir || "") : "";
    entriesToCopy = contextMenuEntries;
    showwingCopyMoveFiles = true;
  }

  function cmdCopyFiles() {
    isMove = false;
    cmdCopyMoveFiles();
  }

  function cmdMoveFiles() {
    isMove = true;
    cmdCopyMoveFiles();
  }

  async function doCopyMoveFiles() {
    console.log("doCopyMoveFiles:", entriesToCopy, "isMove:", isMove);
    let ci = new CopyInfo();
    ci.fsSrc = copyFsFrom;
    ci.fsDst = copyFsTo;
    ci.srcDir = copyFromDir;
    ci.dstDir = copyToDir || "";
    ci.entriesToCopy = entriesToCopy;
    showwingCopyMoveFiles = false;
    try {
      await fsCopyMove(ci, progressMsg, isMove);
    } catch (err: any) {
      console.error("fsCopy failed with", err);
      errorMsg = err.toString();
    }
    progressMsg.set("");

    copyFSStateFrom?.triggerReloadCurrentDir();
    copyFSStateTo?.triggerReloadCurrentDir();
  }

  $effect(() => {
    cmds.openFile = openFile;
    cmds.onFileDrop = onFileDrop;
    cmds.onContextMenu = onContextMenu;
    cmds.showContextMenu = showContextMenu;
    cmds.commands = () => buildMenu(null);
  });
</script>

<input type="file" bind:this={filesEl} multiple hidden onchange={filesSelected} />

{#if showDialogNewDirectory}
  <DialogNewDirectory
    bind:open={showDialogNewDirectory}
    currentDir={fsState.currDir}
    fnNewDirectory={doNewDirectory}
    allowNested={newDirectoryAllowNested()}
  />
{/if}

{#if showDialogDownload}
  <DialogDownload bind:open={showDialogDownload} paths={filesToDownload} />
{/if}
{#if showDialogDelete}
  <DialogDelete bind:open={showDialogDelete} files={filesToDelete} fnDeleteFiles={doDeleteFiles} />
{/if}
{#if showDialogUploadFiles}
  <DialogUploadFiles bind:open={showDialogUploadFiles} files={filesToUpload} />
{/if}
{#if showingFileProperties && filePropertiesEntry}
  <DialogFileProperties bind:open={showingFileProperties} row={filePropertiesEntry} path={filePropertiesPath} />
{/if}

{#if showingFileRename}
  <DialogRename
    bind:open={showingFileRename}
    ignoreCase={fileRenameIgnoreCase}
    destSameDir={fileRenameSameDir}
    path={filePathToRename}
  />
{/if}

{#if showwingCopyMoveFiles}
  <DialogCopyMoveFile
    bind:open={showwingCopyMoveFiles}
    {isMove}
    entries={entriesToCopy}
    fromDir={copyFromFull}
    toDir={copyToFull}
    doit={doCopyMoveFiles}
  />
{/if}

{#if showingImagePreview}
  <Overlay bind:open={showingImagePreview}>
    <ShowImage uri={imagePreviewURL} alt={imagePreviewName} />
  </Overlay>
{/if}

{#if showingTextPreview}
  <Overlay bind:open={showingTextPreview}>
    <ShowText url={textPreviewURL} fileName={textPreviewName} />
  </Overlay>
{/if}
